/**
 * useDetailedHelp Hook
 * 
 * Manages detailed strategic help requests when user explicitly asks for help:
 * - Tracks help request count and enforces limit
 * - Handles explicit help requests (user clicks "Help" button)
 * - Returns detailed coaching evaluation (1-2 paragraphs)
 * - Separate from automatic warnings system
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSessionServices, type ConversationLog } from '../context/SessionServicesContext';
import { EmotionType } from '../types/emotion';
import { getLocalizedCharacterName } from '../utils/characterName';
import { logger } from '../utils/logger';

interface Message {
  speaker: 'user' | 'ai';
  text: string;
  timestamp: number;
}

interface DetailedHelp {
  text: string;
  timestamp: number;
}

interface UseDetailedHelpParams {
  maxHelps: number; // Maximum number of detailed help requests allowed
  sessionStatus: string;
  messages: Message[];
  sessionData: {
    scenario: { id: string };
  } | null;
  sessionStartTime: Date | null;
  sessionDuration: number;
  userId?: string;
  emotionHistory: Array<{ timestamp: number; emotion: EmotionType }>;
}

interface UseDetailedHelpReturn {
  helpsUsed: number;
  maxHelps: number;
  canRequestHelp: boolean; // Can user request help right now?
  requestHelp: () => Promise<void>; // Function to request help
  currentHelp: DetailedHelp | null; // Currently displayed help
  dismissHelp: () => void; // Dismiss current help
  isRequestingHelp: boolean; // Loading state
}

export function useDetailedHelp({
  maxHelps,
  sessionStatus,
  messages,
  sessionData,
  sessionStartTime,
  sessionDuration,
  userId,
  emotionHistory
}: UseDetailedHelpParams): UseDetailedHelpReturn {
  const { requestDetailedHelp, getCurrentLanguage } = useSessionServices();

  const [helpsUsed, setHelpsUsed] = useState<number>(0);
  const [currentHelp, setCurrentHelp] = useState<DetailedHelp | null>(null);
  const [isRequestingHelp, setIsRequestingHelp] = useState<boolean>(false);
  
  // Track AI response count when help was dismissed (for cooldown)
  const [aiResponsesWhenDismissed, setAiResponsesWhenDismissed] = useState<number>(0);
  
  // Track last analyzed exchange index for progressive analysis
  const [lastAnalyzedExchangeIndex, setLastAnalyzedExchangeIndex] = useState<number>(-1);
  
  // Track if we've logged ready state
  const hasLoggedReadyRef = useRef(false);
  
  // Count AI responses
  const aiResponseCount = messages.filter(m => m.speaker === 'ai').length;
  
  // Track previous AI response count to log changes
  const prevAiResponseCountRef = useRef(aiResponseCount);
  useEffect(() => {
    if (aiResponseCount !== prevAiResponseCountRef.current) {
      logger.debug('[DetailedHelp] AI responses:', aiResponseCount);
      prevAiResponseCountRef.current = aiResponseCount;
    }
  }, [aiResponseCount]);

  // Check if enough AI responses have occurred since dismissal
  const responsesSinceDismissal = aiResponseCount - aiResponsesWhenDismissed;
  const enoughResponsesSinceDismissal = aiResponsesWhenDismissed === 0 || responsesSinceDismissal >= 2;

  // Can user request help right now?
  // Requirements:
  // 1. Session is active
  // 2. Haven't exceeded max helps
  // 3. At least 2 AI responses have occurred (not total messages)
  // 4. No help currently displayed
  // 5. Not currently requesting
  // 6. Either: first help request OR 2+ AI responses since last help dismissed
  const canRequestHelp =
    sessionStatus === 'active' &&
    helpsUsed < maxHelps &&
    aiResponseCount >= 2 && // Need at least 2 AI responses
    !currentHelp && // No help currently displayed
    !isRequestingHelp && // Not currently requesting
    enoughResponsesSinceDismissal; // 2+ responses since last dismissal

  // Debug: only log once when ready
  useEffect(() => {
    if (helpsUsed === 0 && aiResponseCount >= 2 && enoughResponsesSinceDismissal && !hasLoggedReadyRef.current) {
      logger.debug('[DetailedHelp] Ready for help requests');
      hasLoggedReadyRef.current = true;
    }
  }, [helpsUsed, aiResponseCount, enoughResponsesSinceDismissal]);

  // Request detailed help
  const requestHelp = useCallback(async () => {
    if (!canRequestHelp || !sessionData) {
      logger.debug('[DetailedHelp] Cannot request help:', {
        canRequestHelp,
        hasSessionData: !!sessionData
      });
      return;
    }

    logger.debug('[DetailedHelp] 🆘 Requesting help...');
    setIsRequestingHelp(true);

    try {
      // Build conversation log
      const conversationLog: ConversationLog = {
        exchanges: messages.map(msg => ({
          speaker: msg.speaker === 'user' ? 'trainee' : 'avatar',
          message: msg.text,
          timestamp: new Date(msg.timestamp).toISOString()
        })),
        metadata: {
          scenarioId: sessionData.scenario.id,
          startTime: new Date(sessionStartTime || Date.now()).toISOString(),
          endTime: new Date().toISOString(),
          userId: userId || 'unknown'
        },
        sessionDuration,
        emotionHistory
      };

      // Get current language
      const language = getCurrentLanguage().split('-')[0]; // 'pt-PT' -> 'pt'

      // Extract character name from session data and localize it
      const characterNameRaw = (sessionData.scenario as any).characterName;
      const characterName = characterNameRaw ? getLocalizedCharacterName(characterNameRaw, getCurrentLanguage()) : undefined;

      // Request detailed help from API
      const result = await requestDetailedHelp(
        sessionData.scenario.id,
        null, // deploymentId - TODO: get from context if available
        conversationLog,
        emotionHistory,
        helpsUsed,
        language,
        characterName,
        lastAnalyzedExchangeIndex >= 0 ? lastAnalyzedExchangeIndex : undefined
      );

      logger.debug('[DetailedHelp] Result received');

      if (result.success && result.help) {
        logger.debug('[DetailedHelp] ✅ Help received');
        const help: DetailedHelp = {
          text: result.help,
          timestamp: Date.now()
        };
        setCurrentHelp(help);
        setHelpsUsed(prev => prev + 1);
        
        // Update last analyzed exchange index to current conversation length
        // This ensures next help request will focus on NEW exchanges
        setLastAnalyzedExchangeIndex(conversationLog.exchanges.length - 1);
        logger.debug('[DetailedHelp] Updated lastAnalyzedExchangeIndex to:', conversationLog.exchanges.length - 1);
      } else {
        console.error('[DetailedHelp] ❌ Failed to get help:', result.error);
        // Could show error to user here
      }

    } catch (error) {
      console.error('[DetailedHelp] ❌ Error requesting help:', error);
    } finally {
      setIsRequestingHelp(false);
    }
  }, [canRequestHelp, sessionData, messages, sessionStartTime, sessionDuration, userId, emotionHistory, helpsUsed]);

  // Dismiss current help
  const dismissHelp = useCallback(() => {
    logger.debug('[DetailedHelp] Dismissing help, current AI responses:', aiResponseCount);
    setCurrentHelp(null);
    // Record current AI response count for cooldown
    setAiResponsesWhenDismissed(aiResponseCount);
  }, [aiResponseCount]);

  return {
    helpsUsed,
    maxHelps,
    canRequestHelp,
    requestHelp,
    currentHelp,
    dismissHelp,
    isRequestingHelp
  };
}

